<template>
  <div class="stores" :class="`stores--${tone}`">
    <a
      v-if="APP_STORE_URL"
      class="store"
      :href="APP_STORE_URL"
      target="_blank"
      rel="noopener"
      aria-label="Download on the App Store"
    >
      <svg class="store__icon" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M16.37 12.62c-.02-2.35 1.92-3.48 2-3.54-1.09-1.6-2.79-1.82-3.4-1.84-1.45-.15-2.83.85-3.56.85-.74 0-1.87-.83-3.07-.81-1.58.02-3.04.92-3.85 2.34-1.64 2.85-.42 7.07 1.18 9.38.78 1.13 1.71 2.4 2.93 2.36 1.18-.05 1.62-.76 3.04-.76 1.42 0 1.82.76 3.07.74 1.27-.02 2.07-1.15 2.84-2.29.9-1.31 1.27-2.58 1.29-2.65-.03-.01-2.47-.95-2.49-3.78zM14.03 5.72c.65-.79 1.09-1.88.97-2.97-.94.04-2.07.62-2.74 1.41-.6.7-1.13 1.81-.99 2.88 1.05.08 2.12-.53 2.76-1.32z"
        />
      </svg>
      <span class="store__text">
        <span class="store__caption">Download on the</span>
        <span class="store__name">App Store</span>
      </span>
    </a>
    <span
      v-else
      class="store store--unavailable"
      aria-label="iOS download link is not available"
    >
      <span class="store__text"
        ><span class="store__caption">iOS</span
        ><span class="store__name">Link coming soon</span></span
      >
    </span>
    <a
      class="store"
      :href="GOOGLE_PLAY_URL"
      target="_blank"
      rel="noopener"
      aria-label="Get it on Google Play"
    >
      <svg class="store__icon" viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          d="M3.61 1.81 13.79 12 3.61 22.19a1.5 1.5 0 0 1-.61-1.21V3.02c0-.49.23-.93.61-1.21zm11.6 8.77 2.63-2.63-11.3-6.52 8.67 9.15zm0 2.84-8.67 9.15 11.3-6.52-2.63-2.63zm4.02-4.66 2.37 1.37c.93.54.93 1.2 0 1.74l-2.37 1.37L16.4 12l2.83-3.24z"
        />
      </svg>
      <span class="store__text">
        <span class="store__caption">Get it on</span>
        <span class="store__name">Google Play</span>
      </span>
    </a>
  </div>
</template>

<script setup lang="ts">
import { APP_STORE_URL, GOOGLE_PLAY_URL } from "@/content/links";

withDefaults(defineProps<{ tone?: "light" | "dark" }>(), { tone: "dark" });
</script>

<style scoped>
.stores {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
}

.store {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 0 24px 0 18px;
  border-radius: 999px;
  transition: transform 180ms ease, background-color 180ms ease,
    border-color 180ms ease;
}

.store--unavailable {
  opacity: 0.65;
}

.store:hover {
  transform: translateY(-1px);
}

.store__icon {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.store__text {
  display: flex;
  flex-direction: column;
  text-align: left;
  line-height: 1.1;
}

.store__caption {
  font-size: 11px;
  font-weight: 400;
  opacity: 0.72;
}

.store__name {
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.01em;
}

/* On dark media: near-white pills */
.stores--light .store {
  background: rgba(255, 255, 255, 0.96);
  color: var(--n-900);
}
.stores--light .store--unavailable {
  opacity: 0.65;
}

.store:hover {
  background: #fff;
}

/* On light surfaces: solid black pills */
.stores--dark .store {
  background: var(--n-900);
  color: #fff;
}
.stores--dark .store--unavailable {
  opacity: 0.65;
}

.store:hover {
  background: #000;
}

@media (max-width: 767px) {
  .store {
    min-height: 48px;
    gap: 8px;
    padding: 0 16px 0 14px;
  }
  .store__icon {
    width: 20px;
    height: 20px;
  }
  .store__caption {
    font-size: 10px;
  }
  .store__name {
    font-size: 15px;
  }
}

@media (max-width: 339px) {
  .stores {
    flex-direction: column;
    align-items: stretch;
  }
  .store {
    justify-content: center;
  }
}
</style>
